import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import styled from "styled-components";
import { CreatorDisplayLayout } from "../common/CreatorDisplayLayout";
import { creatorListAPI, CreatorDisplaySettingResponse, CreatorWithAssignmentResponse } from "@/api/creatorList";
import { createShortUrl, buildShortUrl } from "@/api/shortUrl";
import { hasRole, ROLES } from "@/api/user";
import { creatorAPI } from "@/api/creator";
import { DynamicModal, Icon, LoadingWrapper } from "@/shared/components";
import { extractAvatarUrl } from "@/shared/utils/socialProfile";
import { getFinalRateCurrencies } from "@/shared/utils/displaySettings";
import type { Creator } from "@/shared/types/campaign/creator";

const PageContainer = styled.div`
	padding: 24px;
	background: #eaeefd;
	min-height: 300px;
	border-radius: 16px;
`;

interface CreatorListProps {
	userAssignment?: {
		isAssigned: boolean;
		roles: string[];
	} | null;
}

type DisplayField = {
	key: string;
	label: string;
};

const DEFAULT_DISPLAY_FIELDS: DisplayField[] = [
	{ key: "category", label: "Category" },
	{ key: "creatorTier", label: "Creator Tier" },
	{ key: "sampleContent", label: "Sample Content" },
	{ key: "rationaleForProposing", label: "Rationale For Proposing" },
	{ key: "location", label: "Location" },
];

const HIDDEN_FIELD_PATTERNS = [
	"finalratenumber",
	"defaultratenumber",
	"finalratecurrency",
	"defaultratecurrency",
	"finalrate",
	"defaultrate",
	"finalconvertedrate",
	"final rate number",
	"default rate number",
	"final rate currency",
	"default rate currency",
	"final rate",
	"default rate",
	"final converted rate",
];

const isHiddenField = (key: string, label?: string): boolean => {
	const normalizedKey = key.toLowerCase().replace(/[\s_-]/g, "");
	const normalizedLabel = (label || "").toLowerCase().replace(/[\s_-]/g, "");
	return HIDDEN_FIELD_PATTERNS.some((pattern) => {
		const normalizedPattern = pattern.replace(/[\s_-]/g, "");
		return normalizedKey === normalizedPattern || normalizedLabel === normalizedPattern;
	});
};

const formatLabel = (key: string) => {
	return key
		.replace(/([A-Z])/g, " $1")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (char) => char.toUpperCase())
		.trim();
};

const isFieldVisible = (fieldSetting: any, isClientRole: boolean) => {
	if (!fieldSetting) {
		return true;
	}
	if (fieldSetting.include === false) {
		return false;
	}
	if (isClientRole && fieldSetting.clientVisible === false) {
		return false;
	}
	return true;
};

const toEntries = (value: any): Array<[string, any]> => {
	if (!value) {
		return [];
	}
	if (value instanceof Map) {
		return Array.from(value.entries());
	}
	if (typeof value === "object") {
		return Object.entries(value);
	}
	return [];
};

const deriveDisplayFields = (setting: CreatorDisplaySettingResponse | null, isClientRole: boolean): DisplayField[] => {
	const base = setting?.setting;
	if (!base) {
		return DEFAULT_DISPLAY_FIELDS;
	}

	const entries: Array<{ key: string; label: string; index: number }> = [];

	Object.entries(base).forEach(([key, value]) => {
		if (key === "platformDisplaySettings" || key === "customInfo") {
			return;
		}
		if (isHiddenField(key, value?.label)) {
			return;
		}
		if (!isFieldVisible(value, isClientRole)) {
			return;
		}
		entries.push({
			key,
			label: value?.label || formatLabel(key),
			index: typeof value?.index === "number" ? value.index : Number.MAX_SAFE_INTEGER,
		});
	});

	const customInfoEntries = toEntries(base.customInfo);
	customInfoEntries.forEach(([key, value]) => {
		if (isHiddenField(key, value?.label)) {
			return;
		}
		if (!isFieldVisible(value, isClientRole)) {
			return;
		}
		entries.push({
			key,
			label: value?.label || formatLabel(key),
			index: typeof value?.index === "number" ? value.index : Number.MAX_SAFE_INTEGER,
		});
	});

	if (entries.length === 0) {
		return DEFAULT_DISPLAY_FIELDS;
	}

	return entries.sort((a, b) => a.index - b.index).map(({ key, label }) => ({ key, label }));
};

const BASE_FIELD_EXTRACTORS: Record<string, (data: CreatorWithAssignmentResponse) => any> = {
	category: (data) => data.creator.categories ?? [],
	creatorTier: (data) => data.creator.tier ?? "",
	sampleContent: (data) => data.creator.sampleContents ?? [],
	rationaleForProposing: (data) =>
		data.assignment?.recommendation?.length
			? data.assignment.recommendation.join(", ")
			: (data.creator.rationaleForProposing ?? ""),
	location: (data) => data.creator.location ?? "",
};

const formatCreatorsForDisplay = (source: CreatorWithAssignmentResponse[], fields: DisplayField[]): Creator[] => {
	const effectiveFields = fields.length > 0 ? fields : DEFAULT_DISPLAY_FIELDS;

	return source.map((item) => {
		const row: Creator = {
			id: item.creator.id,
			name: item.creator.name || "N/A",
			contactEmail: item.creator.contactEmail ?? item.assignment?.contactEmail ?? "",
			assignment: item.assignment,
			assignmentId: item.assignment?.id,
		};

		const avatarUrl = extractAvatarUrl(item.socialAccounts);
		if (avatarUrl) {
			row.img_url = avatarUrl;
		}

		effectiveFields.forEach((field) => {
			if (BASE_FIELD_EXTRACTORS[field.key]) {
				const value = BASE_FIELD_EXTRACTORS[field.key](item);
				// Keep arrays as-is so grid view can render them as lists; table view will format arrays itself
				row[field.key] = value;
			} else if (item.customInfo && Object.prototype.hasOwnProperty.call(item.customInfo, field.key)) {
				row[field.key] = item.customInfo[field.key];
			} else {
				row[field.key] = "";
			}
		});

		// Attach custom info fields that might not be part of displayFields yet
		Object.entries(item.customInfo || {}).forEach(([key, value]) => {
			if (!row[key]) {
				row[key] = value;
			}
		});

		// Add socialAccounts as a property for easy access
		row.socialAccounts = item.socialAccounts || {};

		// Add socialAccounts as a property for easy access
		row.socialAccounts = item.socialAccounts || {};

		// Attach social account data per platform
		Object.entries(item.socialAccounts || {}).forEach(([platform, account]) => {
			const normalizedPlatform = platform.toLowerCase();
			row[normalizedPlatform] = {
				followers: account.followers,
				avgEngagementRate: account.avgEngagementRate,
				avgViews: account.avgViews,
				localAudiencePercentage: account.localAudiencePercentage,
				femalePercentage: account.femalePercentage,
				malePercentage: account.malePercentage,
				audienceAgeDistribution: account.audienceAgeDistribution,
				platformUsername: account.platformUsername,
			};
		});

		// Add assignment data including recommendation
		if (item.assignment) {
			row.recommendation = item.assignment.recommendation || [];
		}

		// Convenience field for legacy "rationale" usage
		if (!row.rationale && row.rationaleForProposing) {
			row.rationale = row.rationaleForProposing;
		}

		return row;
	});
};

export const CreatorList: React.FC<CreatorListProps> = ({ userAssignment }) => {
	const [searchParams] = useSearchParams();
	const campaignId = searchParams.get("campaignId");
	const blockId = searchParams.get("blockId");
	const navigate = useNavigate();
	const [rawCreators, setRawCreators] = useState<CreatorWithAssignmentResponse[]>([]);
	const [creators, setCreators] = useState<Creator[]>([]);
	const [listId, setListId] = useState<string>("");
	const [clientVisible, setClientVisible] = useState(true);
	const [viewMode, setViewMode] = useState<"table" | "grid">("grid");
	const [creatorVisibility, setCreatorVisibility] = useState<{ [key: string]: boolean }>({});
	const [isCustomizing, setIsCustomizing] = useState(false);
	const [orderedCreators, setOrderedCreators] = useState<Creator[]>([]);
	const [savedVisibility, setSavedVisibility] = useState<{ [key: string]: boolean }>({});
	const [savedClientVisible, setSavedClientVisible] = useState(true);
	const [displaySetting, setDisplaySetting] = useState<CreatorDisplaySettingResponse | null>(null);
	const [displayFields, setDisplayFields] = useState<DisplayField[]>(DEFAULT_DISPLAY_FIELDS);
	const [loading, setLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [removeModal, setRemoveModal] = useState<{ assignmentId: string; name: string } | null>(null);
	const [removing, setRemoving] = useState(false);

	// Parent handler to persist canonical updates from children
	const handleUpdateCreator = (updated: Partial<any>) => {
		if (!updated?.id) return;
		setRawCreators((prev) =>
			prev.map((item) => {
				if (item.creator.id !== updated.id) return item;
				const next: any = { ...item };
				if (updated.recommendation !== undefined) {
					next.assignment = { ...(next.assignment || {}), recommendation: updated.recommendation };
				}
				if (updated.contactEmail !== undefined) {
					next.assignment = { ...(next.assignment || {}), contactEmail: updated.contactEmail };
					next.creator = { ...(next.creator || {}), contactEmail: updated.contactEmail };
				}
				if (updated.name !== undefined || updated.img_url !== undefined) {
					next.creator = {
						...(next.creator || {}),
						...(updated.name !== undefined ? { name: updated.name } : {}),
						...(updated.img_url !== undefined ? { img_url: updated.img_url } : {}),
					};
				}
				return next;
			}),
		);
	};

	const handleCategoryTierChange = (creatorId: string, field: "categories" | "tier", value: string[] | string) => {
		if (!campaignId) return;

		// Optimistic: update local state immediately
		setRawCreators((prev) =>
			prev.map((item) => {
				if (item.creator.id !== creatorId) return item;
				return {
					...item,
					creator: {
						...item.creator,
						...(field === "categories" && { categories: value as typeof item.creator.categories }),
						...(field === "tier" && { tier: value as typeof item.creator.tier }),
					},
				};
			}),
		);

		// Fire API in background, revert on failure
		const apiCall =
			field === "categories"
				? creatorAPI.updateCreatorCategories(creatorId, value as string[], campaignId)
				: creatorAPI.updateCreatorTier(creatorId, value as string, campaignId);

		apiCall.catch(() => {
			message.error(`Failed to update ${field === "categories" ? "category" : "tier"}`);
			if (listId) loadCreatorsFromList(listId);
		});
	};

	const campaignRoles: string[] = (() => {
		try {
			return JSON.parse(sessionStorage.getItem("campaignRoles") || "[]");
		} catch {
			return [];
		}
	})();

	const STAFF_ROLES = ["Lead", "Support", "Account Manager"];
	const isClientRole = campaignRoles.some((role: string) => role?.toLowerCase() === "client");
	const isClientOnly =
		campaignRoles.some((r: string) => r.toLowerCase() === "client") &&
		!campaignRoles.some((r: string) => STAFF_ROLES.includes(r));
	const isAgencyInCampaign = campaignRoles.some((r: string) => STAFF_ROLES.includes(r));
	const canManage = hasRole(ROLES.ADMIN) || isAgencyInCampaign;

	const loadCreatorsFromList = useCallback(
		async (listId: string) => {
			if (!campaignId || !blockId) {
				console.warn("Missing campaignId or blockId");
				return;
			}

			try {
				const creatorsData = await creatorListAPI.getCreatorsFromList(campaignId, blockId, listId);
				setRawCreators(creatorsData);
				setClientVisible(creatorsData.some((item) => item.assignment?.clientVisible === true));
			} catch (error) {
				console.error("Error loading creators from list:", error);
				message.error((error as Error).message || "Failed to load creators from list");
			}
		},
		[campaignId, blockId],
	);

	const loadDisplaySetting = useCallback(
		async (displayId: string) => {
			if (!campaignId || !blockId) {
				return null;
			}

			try {
				const response = await creatorListAPI.getCreatorDisplaySetting(campaignId, blockId, displayId);
				setDisplaySetting(response);
				return response;
			} catch (error) {
				console.error("Error loading display settings:", error);
				return null;
			}
		},
		[campaignId, blockId],
	);

	// Single effect to load everything - only depends on campaignId and blockId
	useEffect(() => {
		const loadData = async () => {
			if (!campaignId || !blockId) {
				setLoading(false);
				return;
			}

			try {
				setLoading(true);

				// Fetch list data
				const listData = await creatorListAPI.getCreatorLists(campaignId, blockId);

				if (listData && listData.id) {
					const id = listData.id;
					setListId(id);

					// Load display settings and creators in parallel
					await Promise.all([loadDisplaySetting(id), loadCreatorsFromList(id)]);
				}
			} catch (error) {
				console.error("Error loading creator list data:", error);
				message.error((error as Error).message || "Failed to load creator list");
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, [campaignId, blockId, loadCreatorsFromList, loadDisplaySetting]);

	useEffect(() => {
		setDisplayFields(deriveDisplayFields(displaySetting, isClientRole));
	}, [displaySetting, isClientRole]);

	const { finalRateCurrency, finalRateConvertedCurrency } = useMemo(
		() => getFinalRateCurrencies(displaySetting),
		[displaySetting],
	);

	const tableDisplayFields = useMemo(
		() => displayFields.filter((f) => f.key !== "rationaleForProposing"),
		[displayFields],
	);

	useEffect(() => {
		setCreators(formatCreatorsForDisplay(rawCreators, displayFields));
	}, [rawCreators, displayFields]);

	const handleAddCreator = () => {
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}
		if (!listId) {
			message.error("Creator list is not initialized yet. Please try again.");
			return;
		}
		navigate(`/campaign-detail/add-creator?campaignId=${campaignId}&blockId=${blockId}&listId=${listId}`);
	};

	const handleGetUrl = async () => {
		const path = window.location.pathname + window.location.search;
		try {
			const shortUrl = await createShortUrl(path);
			await navigator.clipboard.writeText(buildShortUrl(shortUrl.code, path));
			message.success("Link copied to clipboard!");
		} catch {
			await navigator.clipboard.writeText(path);
			message.success("Link copied to clipboard!");
		}
	};

	const handleCustomizeOrder = () => {
		if (isCustomizing) {
			setIsCustomizing(false);
			setCreatorVisibility(savedVisibility);
			setClientVisible(savedClientVisible);
			message.info("Customization cancelled");
		} else {
			// Start customization
			setOrderedCreators([...creators]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
			message.info("Drag rows to reorder");
		}
	};

	const handleSaveOrder = async () => {
		if (isSaving) return;
		setIsSaving(true);
		try {
			const updates = rawCreators
				.filter((item) => item.assignment?.id)
				.map((item) => ({
					assignmentId: item.assignment!.id,
					clientVisible: creatorVisibility[item.creator?.id ?? ""] ?? true,
				}));

			await creatorListAPI.updateClientVisibleToCreatorAssignments(campaignId!, blockId!, updates);

			setCreators(orderedCreators);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(false);
			message.success("Changes saved successfully!");
		} catch {
			message.error("Failed to save changes");
		} finally {
			setIsSaving(false);
		}
	};

	const handleClientVisibleToggle = () => {
		if (!isCustomizing) {
			setOrderedCreators([...creators]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
		}

		const newClientVisible = !clientVisible;
		setClientVisible(newClientVisible);

		// When turning on "Visible to Client", set all creator toggles to true
		if (newClientVisible) {
			const allVisible: { [key: string]: boolean } = {};
			creators.forEach((creator) => {
				allVisible[creator.id] = true;
			});
			setCreatorVisibility(allVisible);
		} else {
			// When turning off "Visible to Client", set all creator toggles to false
			const allInvisible: { [key: string]: boolean } = {};
			creators.forEach((creator) => {
				allInvisible[creator.id] = false;
			});
			setCreatorVisibility(allInvisible);
		}
	};

	const handleCreatorVisibilityToggle = (creatorId: string) => {
		if (!isCustomizing) {
			setOrderedCreators([...creators]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
		}

		const newVisibility = {
			...creatorVisibility,
			[creatorId]: !creatorVisibility[creatorId],
		};
		setCreatorVisibility(newVisibility);

		// Check if all creators are now invisible
		const allInvisible = creators.every((creator) => !newVisibility[creator.id]);

		// If all creators are invisible, turn off the main toggle
		if (allInvisible && clientVisible) {
			setClientVisible(false);
		}
	};

	const handleSettingsClick = (creatorId: string) => {
		console.log("Settings clicked for creator:", creatorId);
		message.info("Creator settings functionality coming soon");
	};

	const handleCommentClick = (creatorId: string) => {
		console.log("Comment clicked for creator:", creatorId);
		message.info("Comments functionality coming soon");
	};

	const handleCustomizeColumns = () => {
		navigate(
			`/campaign-detail/customize-columns?campaignId=${campaignId}&blockId=${blockId}&displayId=${listId}&pageTitle=Creator List`,
		);
	};

	const handleCustomizeOrderButton = () => {
		// For grid view - same as handleCustomizeOrder
		handleCustomizeOrder();
	};

	const handleRemoveFromCampaign = (assignmentId: string, creatorName?: string) => {
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		const name = creatorName || "Creator";
		setRemoveModal({ assignmentId, name });
	};

	// Filter creators for client-only view
	const displayCreators = isClientOnly
		? creators.filter((creator) => creatorVisibility[creator.id] !== false)
		: isCustomizing
			? orderedCreators
			: creators;

	// Initialize ordered creators and ensure visibility defaults exist
	useEffect(() => {
		setOrderedCreators(creators);

		setCreatorVisibility((prev) => {
			const nextVisibility = { ...prev };
			let hasChanges = false;

			rawCreators.forEach((item) => {
				const creatorId = item.creator?.id;
				if (creatorId && nextVisibility[creatorId] === undefined) {
					nextVisibility[creatorId] = item.assignment?.clientVisible ?? false;
					hasChanges = true;
				}
			});

			return hasChanges ? nextVisibility : prev;
		});
	}, [creators]);

	if (loading) {
		return (
			<LoadingWrapper isLoading label="Loading creator list" borderRadius={12}>
				<PageContainer />
			</LoadingWrapper>
		);
	}

	return (
		<>
			<CreatorDisplayLayout
				pageTitle="Creator List"
				addCreatorText="Add/Edit Creators to Creator List"
				displayId={listId}
				userAssignment={userAssignment}
				campaignId={campaignId}
				blockId={blockId}
				displayCreators={displayCreators}
				clientVisible={clientVisible}
				viewMode={viewMode}
				creatorVisibility={creatorVisibility}
				isCustomizing={isCustomizing}
				isSaving={isSaving}
				displayFields={tableDisplayFields}
				canManage={canManage}
				onAddCreator={handleAddCreator}
				onGetUrl={handleGetUrl}
				onCustomizeOrder={handleCustomizeOrder}
				onSaveOrder={handleSaveOrder}
				onClientVisibleToggle={handleClientVisibleToggle}
				onCreatorVisibilityToggle={handleCreatorVisibilityToggle}
				onSettingsClick={handleSettingsClick}
				onCommentClick={handleCommentClick}
				onCustomizeColumns={handleCustomizeColumns}
				onCustomizeOrderButton={handleCustomizeOrderButton}
				onRemoveFromCampaign={handleRemoveFromCampaign}
				onViewModeChange={setViewMode}
				onOrderChange={setOrderedCreators}
				onCategoryTierChange={handleCategoryTierChange}
				onUpdateCreator={handleUpdateCreator}
				finalRateCurrency={finalRateCurrency}
				finalRateConvertedCurrency={finalRateConvertedCurrency}
			/>
			<DynamicModal
				open={!!removeModal}
				onCancel={() => setRemoveModal(null)}
				title={"Remove from campaign"}
				description={
					removeModal
						? `Removing ${removeModal.name} will permanently delete all contents linked to the creator in this campaign. This action can’t be undone.`
						: undefined
				}
				icon={<Icon src="/icon/trash.svg" size={36} color="#D30024" />}
				confirmText="Remove"
				cancelText="Cancel"
				loading={removing}
				variant="danger"
				onConfirm={async () => {
					if (!removeModal) return;
					if (!campaignId || !blockId) {
						message.error("Missing campaign or block information");
						return;
					}
					setRemoving(true);
					try {
						await creatorListAPI.removeCreatorFromCampaign(campaignId, blockId, removeModal.assignmentId);
						message.success("Creator removed from campaign successfully!");
						setRawCreators((prev) =>
							prev.filter((item) => item.assignment?.id !== removeModal.assignmentId),
						);
						setRemoveModal(null);
					} catch (error) {
						console.error("Error removing creator from campaign:", error);
						message.error("Failed to remove creator from campaign");
					} finally {
						setRemoving(false);
					}
				}}
			/>
		</>
	);
};
